/* Müşteri Soruları — Manuel paste + AI taslak yanıt.
 * E-Ticaret tabı altında sekme olarak görünür. Bol'da soru gelince:
 * 1. Soruyu kopyala → buraya yapıştır
 * 2. (Opsiyonel) Sipariş # gir → AI gerçek kargo durumunu okur
 * 3. AI taslak üretir (Hollandaca/İngilizce vs)
 * 4. Düzenle veya direkt kopyala → Bol panele yapıştır
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { safeCopy } from '../../../utils/clipboard';
import {
    Sparkles, Copy, Loader2, Languages, RotateCcw, MessageSquare, Package, Truck,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const HDR = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const LANG_OPTIONS = [
    { v: 'nl', l: '🇳🇱 Hollandaca' },
    { v: 'en', l: '🇬🇧 İngilizce' },
    { v: 'de', l: '🇩🇪 Almanca' },
    { v: 'fr', l: '🇫🇷 Fransızca' },
];

const TONE_OPTIONS = [
    { v: 'polite', l: 'Nazik' },
    { v: 'apologetic', l: 'Özür dileyen' },
    { v: 'short', l: 'Kısa & öz' },
    { v: 'detailed', l: 'Detaylı' },
];

export default function CustomerQATab() {
    const [question, setQuestion] = useState('');
    const [orderId, setOrderId] = useState('');
    const [language, setLanguage] = useState('nl');
    const [tone, setTone] = useState('polite');
    const [reply, setReply] = useState('');
    const [trReply, setTrReply] = useState('');
    const [contextUsed, setContextUsed] = useState(null);
    const [busy, setBusy] = useState(false);
    const [translating, setTranslating] = useState(false);

    const generate = async () => {
        if (!question.trim() || question.trim().length < 3) {
            toast.error('Soru metni gerekli');
            return;
        }
        setBusy(true);
        setReply('');
        setTrReply('');
        setContextUsed(null);
        try {
            const r = await axios.post(`${API}/api/bol/customer-qa/draft`, {
                question, order_id: orderId.trim(), language, tone,
            }, { headers: HDR(), timeout: 60000 });
            setReply(r.data.reply || '');
            setContextUsed(r.data.context_used || null);
            if (!r.data.has_context && orderId.trim()) {
                toast.warning('Sipariş bulunamadı, AI genel cevap üretti');
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || 'AI taslak üretmedi');
        } finally { setBusy(false); }
    };

    const translateToTr = async () => {
        if (!reply.trim()) return;
        setTranslating(true);
        try {
            const r = await axios.post(`${API}/api/bol/customer-qa/translate`,
                { text: reply, target: 'tr' }, { headers: HDR(), timeout: 30000 });
            setTrReply(r.data.translated || '');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Çeviri hatası');
        } finally { setTranslating(false); }
    };

    const copyReply = () => {
        safeCopy(reply, 'Yanıt kopyalandı — Bol paneline yapıştır');
    };

    const reset = () => {
        setQuestion(''); setOrderId(''); setReply(''); setTrReply(''); setContextUsed(null);
    };

    return (
        <div className="space-y-4" data-testid="customer-qa-tab">
            <div className="bg-gradient-to-r from-indigo-50 via-white to-emerald-50 border border-indigo-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                        <MessageSquare size={18} className="text-indigo-600" />
                    </div>
                    <div className="flex-1">
                        <h2 className="text-base font-bold text-gray-900">Müşteri Sorusu Yanıtla</h2>
                        <p className="text-xs text-gray-600 mt-0.5">
                            Bol panelinde soruyu kopyala → buraya yapıştır → AI taslak üretir → kopyala → Bol&apos;a yapıştır.
                            Sipariş # girersen AI <strong>gerçek kargo durumunu</strong> okur.
                        </p>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* SOL — INPUT */}
                <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
                    <Field label="Soru (Bol'dan kopyala-yapıştır) *">
                        <textarea
                            value={question} onChange={(e) => setQuestion(e.target.value)}
                            placeholder="Hallo, waar is mijn pakketje?..."
                            rows={6}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:border-indigo-500 outline-none resize-y"
                            data-testid="qa-question" />
                    </Field>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Field label="Sipariş # (opsiyonel)" hint="AI gerçek kargo durumunu okur">
                            <input
                                value={orderId} onChange={(e) => setOrderId(e.target.value.toUpperCase())}
                                placeholder="C00071..."
                                className="w-full h-10 px-3 border border-gray-300 rounded-md text-sm font-mono uppercase"
                                data-testid="qa-order-id" />
                        </Field>
                        <Field label="Dil">
                            <select value={language} onChange={(e) => setLanguage(e.target.value)}
                                className="w-full h-10 px-2 border border-gray-300 rounded-md text-sm"
                                data-testid="qa-language">
                                {LANG_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                            </select>
                        </Field>
                    </div>

                    <Field label="Ton">
                        <div className="flex flex-wrap gap-1.5" data-testid="qa-tones">
                            {TONE_OPTIONS.map((o) => (
                                <button key={o.v} onClick={() => setTone(o.v)}
                                    className={`h-8 px-2.5 text-xs font-bold rounded-md border transition ${
                                        tone === o.v
                                            ? 'bg-indigo-600 border-indigo-600 text-white'
                                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                                    }`}>
                                    {o.l}
                                </button>
                            ))}
                        </div>
                    </Field>

                    <div className="flex gap-2 pt-2">
                        <button onClick={generate} disabled={busy || !question.trim()}
                            className="flex-1 h-11 px-4 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-bold rounded-md inline-flex items-center justify-center gap-2"
                            data-testid="qa-generate">
                            {busy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                            {busy ? 'AI çalışıyor...' : 'AI Taslak Üret'}
                        </button>
                        {(question || reply) && (
                            <button onClick={reset}
                                className="h-11 px-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md inline-flex items-center gap-1 text-xs font-bold"
                                data-testid="qa-reset">
                                <RotateCcw size={12} /> Temizle
                            </button>
                        )}
                    </div>
                </div>

                {/* SAĞ — OUTPUT */}
                <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
                    {contextUsed && contextUsed.latest_event && (
                        <div className="bg-emerald-50 border border-emerald-200 rounded-md p-2.5 text-xs flex items-start gap-2" data-testid="qa-context-card">
                            <Truck size={12} className="text-emerald-700 mt-0.5 shrink-0" />
                            <div>
                                <div className="font-bold text-emerald-900">Bu sipariş için bağlam yüklendi</div>
                                <div className="text-emerald-700 mt-0.5">
                                    Durum: <strong>{contextUsed.latest_event}</strong>
                                    {contextUsed.latest_event_at && ` (${new Date(contextUsed.latest_event_at).toLocaleString('tr-TR')})`}
                                </div>
                                {contextUsed.products?.length > 0 && (
                                    <div className="text-emerald-700 mt-0.5 inline-flex items-center gap-1">
                                        <Package size={10} /> {contextUsed.products[0].title}
                                        {contextUsed.products.length > 1 && ` +${contextUsed.products.length - 1}`}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <Field label={`AI Taslak (${LANG_OPTIONS.find((o) => o.v === language)?.l || ''})`}>
                        <textarea
                            value={reply} onChange={(e) => setReply(e.target.value)}
                            placeholder={busy ? 'AI yanıt hazırlıyor...' : 'AI taslak yanıt burada görünecek. Düzenleyebilirsin.'}
                            rows={10}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm leading-relaxed focus:border-indigo-500 outline-none resize-y font-mono"
                            data-testid="qa-reply" />
                    </Field>

                    <div className="flex flex-wrap gap-2">
                        <button onClick={copyReply} disabled={!reply.trim()}
                            className="flex-1 h-10 px-4 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white font-bold rounded-md inline-flex items-center justify-center gap-2"
                            data-testid="qa-copy">
                            <Copy size={14} /> Yanıtı Kopyala
                        </button>
                        <button onClick={translateToTr} disabled={!reply.trim() || translating || language === 'tr'}
                            className="h-10 px-3 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-40 text-gray-700 rounded-md inline-flex items-center gap-1 text-xs font-bold"
                            data-testid="qa-translate-tr">
                            {translating ? <Loader2 size={12} className="animate-spin" /> : <Languages size={12} />}
                            🇹🇷 Türkçe Önizleme
                        </button>
                    </div>

                    {trReply && (
                        <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-xs text-gray-700 whitespace-pre-wrap" data-testid="qa-tr-preview">
                            <div className="font-bold text-gray-500 uppercase text-[10px] mb-1">🇹🇷 Türkçe Önizleme (sadece kontrol için)</div>
                            {trReply}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function Field({ label, hint, children }) {
    return (
        <div>
            <label className="text-[10px] font-bold uppercase text-gray-500 tracking-wide block mb-1">
                {label} {hint && <span className="text-gray-400 normal-case font-normal">— {hint}</span>}
            </label>
            {children}
        </div>
    );
}
